/**
 * In-place edits to a .canary.md spec file: write the workflow ID into
 * frontmatter on first compile, and write `<!-- @id <uuid> -->` next to
 * newly-created node lead-ins so recompile can key the diff on identity.
 *
 * Pure string transforms — `index.ts` reads/writes the file.
 *
 * @module spec/writeback
 */

const FRONTMATTER_FENCE = /^---\s*$/;

/**
 * Insert `workflowId: <uuid>` into the spec's frontmatter, immediately
 * after the `name:` line. If `workflowId:` already exists, returns the
 * source unchanged.
 */
export function writeWorkflowId(source: string, workflowId: string): string {
  const lines = source.split(/\r?\n/);
  if (!FRONTMATTER_FENCE.test(lines[0] ?? '')) return source;
  const end = findFrontmatterEnd(lines);
  if (end === -1) return source;

  // Already present?
  for (let i = 1; i < end; i++) {
    if (/^workflowId\s*:/.test(lines[i])) return source;
  }

  // Insert after the first `name:` line, or fall back to the line before the fence.
  let insertAt = end;
  for (let i = 1; i < end; i++) {
    if (/^name\s*:/.test(lines[i])) {
      insertAt = i + 1;
      break;
    }
  }

  const out = [...lines];
  out.splice(insertAt, 0, `workflowId: ${workflowId}`);
  return out.join('\n');
}

/**
 * Insert `<!-- @id <uuid> -->` at the end of each lead-in line for the
 * given (sourceLine → nodeId) pairs. Lines are 0-indexed and refer to
 * line numbers in the **original** source (the same numbers the parser
 * recorded in `node.sourceLine`).
 *
 * Skips any line that already has an `@id` comment (idempotent).
 */
export function writeNodeIds(source: string, pairs: Array<{ sourceLine: number; nodeId: string }>): string {
  const lines = source.split(/\r?\n/);
  const NODE_ID_RE = /<!--\s*@id\s+[0-9a-f-]+\s*-->/i;
  for (const { sourceLine, nodeId } of pairs) {
    if (sourceLine < 0 || sourceLine >= lines.length) continue;
    const line = lines[sourceLine];
    if (NODE_ID_RE.test(line)) continue;
    // Trim trailing whitespace, append the comment.
    lines[sourceLine] = `${line.trimEnd()}   <!-- @id ${nodeId} -->`;
  }
  return lines.join('\n');
}

function findFrontmatterEnd(lines: string[]): number {
  if (!FRONTMATTER_FENCE.test(lines[0] ?? '')) return -1;
  for (let i = 1; i < lines.length; i++) if (FRONTMATTER_FENCE.test(lines[i])) return i;
  return -1;
}
